import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, ListTodo } from 'lucide-react';
import { format } from 'date-fns';
import { da, enUS } from 'date-fns/locale';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { ListRow } from '@/components/ui/Card';
import { FilterChip, StatusChip, type StatusTone } from '@/components/ui/Chip';
import { TaskFormSlideOver } from '@/components/tasks/TaskFormSlideOver';
import { ConfirmActionModal, PatientTabMoreMenu } from '@/components/patient/PatientTabActions';
import { PatientTabFrame, PatientTabToolbar } from '@/components/patient/PatientTabFrame';
import { MenuItem } from '@/components/ui/Popover';
import {
  useDeleteStaffTask,
  useStaffTasksForEntity,
  useToggleStaffTaskDone,
} from '@/hooks/useStaffTasks';
import {
  STAFF_TASK_STATUSES,
  staffTaskIsOverdue,
  type StaffTask,
  type StaffTaskStatus,
} from '@/lib/staffTasks';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

interface TaskTabProps {
  patientId: string;
  clinicId: string;
}

function taskStatusTone(status: StaffTaskStatus): StatusTone {
  switch (status) {
    case 'open':
      return 'upcoming';
    case 'in_progress':
      return 'draft';
    case 'done':
      return 'completed';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function taskPriorityTone(priority: StaffTask['priority']): StatusTone {
  switch (priority) {
    case 'high':
      return 'coral';
    case 'normal':
      return 'draft';
    case 'low':
      return 'neutral';
    default: {
      const _exhaustive: never = priority;
      return _exhaustive;
    }
  }
}

export function TaskTab({ patientId, clinicId }: TaskTabProps) {
  const { t, i18n } = useTranslation();
  const { data: tasks, isLoading, error, refetch } = useStaffTasksForEntity(
    clinicId,
    'patient',
    patientId,
  );
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<StaffTask | null>(null);
  const [statusFilter, setStatusFilter] = useState<StaffTaskStatus | 'all'>('all');
  const dateLocale = i18n.language === 'da' ? da : enUS;

  const counts = useMemo(() => {
    const list = tasks ?? [];
    return {
      all: list.length,
      open: list.filter((task) => task.status === 'open').length,
      in_progress: list.filter((task) => task.status === 'in_progress').length,
      done: list.filter((task) => task.status === 'done').length,
    };
  }, [tasks]);

  const filtered =
    tasks?.filter((task) => statusFilter === 'all' || task.status === statusFilter) ?? [];
  const empty = !isLoading && !error && (!tasks || tasks.length === 0);

  return (
    <>
      <PatientTabFrame
        title={t('patients.tabs.tasks')}
        count={counts.all}
        action={
          <Button type="button" onClick={() => setShowCreate(true)}>
            {t('tasks.create')}
          </Button>
        }
        loading={isLoading}
        error={error ? t('tasks.error_loading') : undefined}
        onRetry={() => void refetch()}
        toolbar={
          <PatientTabToolbar
            chips={
              <div className="flex gap-s2" aria-label={t('tasks.filter_status')}>
                {(['all', ...STAFF_TASK_STATUSES] as const).map((status) => (
                  <FilterChip
                    key={status}
                    pressed={statusFilter === status}
                    count={status === 'all' ? counts.all : counts[status]}
                    onClick={() => setStatusFilter(status)}
                  >
                    {status === 'all' ? t('tasks.filter_all') : t(`tasks.status.${status}`)}
                  </FilterChip>
                ))}
              </div>
            }
          />
        }
        empty={
          empty
            ? {
                icon: <ListTodo className="h-7 w-7" />,
                title: t('tasks.empty_title'),
                description: t('tasks.empty_description'),
              }
            : undefined
        }
      >
        {filtered.length === 0 ? (
          <p className={cn(TYPE.body, 'mt-s5 text-ds-muted')}>
            {t('tasks.empty_filtered', {
              status:
                statusFilter === 'all' ? t('tasks.filter_all') : t(`tasks.status.${statusFilter}`),
            })}
          </p>
        ) : (
          <div className="mt-s4">
            {filtered.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                clinicId={clinicId}
                dateLocale={dateLocale}
                onEdit={() => setEditing(task)}
              />
            ))}
          </div>
        )}
      </PatientTabFrame>

      {showCreate ? (
        <TaskFormSlideOver
          clinicId={clinicId}
          presetLinks={[{ entity_type: 'patient', entity_id: patientId }]}
          onClose={() => setShowCreate(false)}
        />
      ) : null}
      {editing ? (
        <TaskFormSlideOver clinicId={clinicId} task={editing} onClose={() => setEditing(null)} />
      ) : null}
    </>
  );
}

function TaskRow({
  task,
  clinicId,
  onEdit,
  dateLocale,
}: {
  task: StaffTask;
  clinicId: string;
  onEdit: () => void;
  dateLocale: typeof da;
}) {
  const { t } = useTranslation();
  const toggleDone = useToggleStaffTaskDone();
  const deleteTask = useDeleteStaffTask();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const overdue = staffTaskIsOverdue(task.deadline_date, task.deadline_time, task.status);

  const handleToggleComplete = async () => {
    try {
      await toggleDone.mutateAsync({
        task_id: task.id,
        clinic_id: clinicId,
        done: task.status !== 'done',
      });
      toast.success(task.status === 'done' ? t('tasks.toast_reopened') : t('tasks.toast_completed'));
    } catch {
      toast.error(t('tasks.error_update'));
    }
  };

  return (
    <ListRow
      rail={task.status === 'done' ? 'finalized' : overdue ? 'cancelled' : 'draft'}
      className="hover:bg-ds-sunk"
    >
      <button
        type="button"
        onClick={() => void handleToggleComplete()}
        disabled={toggleDone.isPending}
        className="shrink-0 text-ds-teal disabled:opacity-50"
        aria-label={task.status === 'done' ? t('tasks.mark_open') : t('tasks.mark_done')}
      >
        <CheckCircle
          className={cn('h-5 w-5', task.status === 'done' ? 'text-ds-green-ink' : 'text-ds-line')}
        />
      </button>
      <button type="button" onClick={onEdit} className="min-w-0 flex-1 text-left">
        <p
          className={cn(
            TYPE.body,
            'text-ds-text',
            task.status === 'done' && 'text-ds-muted line-through',
          )}
        >
          {task.title}
        </p>
        <p className={cn(TYPE.small, overdue ? 'text-ds-danger' : 'text-ds-muted')}>
          {[
            task.assignees.length > 0
              ? `${t('tasks.field_assignees')}: ${task.assignees.map((assignee) => assignee.name).join(', ')}`
              : t('tasks.unassigned'),
            task.deadline_date
              ? `${t('tasks.field_deadline_date')}: ${format(new Date(`${task.deadline_date}T00:00:00`), 'dd/MM/yyyy', { locale: dateLocale })}`
              : null,
            overdue ? t('tasks.overdue') : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </button>
      <div className="flex shrink-0 flex-wrap items-center gap-s2">
        <StatusChip tone={taskPriorityTone(task.priority)}>
          {t(`tasks.priority.${task.priority}`)}
        </StatusChip>
        <StatusChip tone={taskStatusTone(task.status)}>{t(`tasks.status.${task.status}`)}</StatusChip>
        <PatientTabMoreMenu label={t('patients.tab_frame.more_actions_for', { name: task.title })}>
          <MenuItem onClick={onEdit}>{t('common.edit')}</MenuItem>
          <MenuItem danger onClick={() => setConfirmOpen(true)}>
            {t('common.delete')}
          </MenuItem>
        </PatientTabMoreMenu>
      </div>
      <ConfirmActionModal
        open={confirmOpen}
        title={t('tasks.confirm_delete')}
        confirmLabel={t('common.delete')}
        pending={deleteTask.isPending}
        onConfirm={() => {
          void deleteTask
            .mutateAsync({ task_id: task.id, clinic_id: clinicId })
            .then(() => {
              toast.success(t('tasks.toast_deleted'));
              setConfirmOpen(false);
            })
            .catch(() => toast.error(t('tasks.error_delete')));
        }}
        onClose={() => setConfirmOpen(false)}
      />
    </ListRow>
  );
}
